import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { Observable, Subject, takeUntil } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { IntegrationsApiService } from '../data/integrations-api.service';
import { CONNECTOR_KEY } from '../catalog/provider-catalog';
import {
  IG_OAUTH_CALLBACK_TYPE,
  type InstagramOAuthCallbackMessage,
} from '../pages/instagram-callback.page';
import type { ConnectContext, ConnectorStrategy } from './connector-strategy';
import type { ConnectResult } from '../domain/connect-result';

const IG_AUTHORIZE_URL = 'https://www.instagram.com/oauth/authorize';
// Precisa bater com INSTAGRAM_BUSINESS_SCOPES (automato-api/.../providers/instagram.adapter.ts) —
// pedir menos escopo aqui do que o backend declara como obrigatório passa no OAuth mas falha
// silenciosamente na primeira ação que precisa do escopo faltante (ex.: manage_messages só falha
// na hora de enviar a DM, não na conexão), sem aviso nenhum até o usuário tentar usar a feature.
const IG_SCOPE = 'instagram_business_basic,instagram_business_manage_messages,instagram_business_manage_insights';
const POPUP_FEATURES = 'width=600,height=760,menubar=no,toolbar=no';
const POPUP_POLL_MS = 500;

/**
 * Connects an Instagram professional account via Business Login for Instagram.
 *
 * Mirrors {@link MetaWhatsAppConnector} but without the Facebook JS SDK: it opens
 * the Instagram authorize URL in a popup, receives the authorization code from
 * the redirect callback page via postMessage, and exchanges it on the backend.
 */
@Injectable({ providedIn: 'root' })
export class MetaInstagramConnector implements ConnectorStrategy {
  readonly key = CONNECTOR_KEY.metaInstagram;
  readonly introHint =
    'Vamos abrir o login do Instagram em uma nova janela. Faça login com a conta profissional que deseja conectar e autorize o acesso às mensagens diretas.';
  readonly cancelledHint = 'Você fechou a janela do Instagram sem concluir.';

  private readonly document = inject(DOCUMENT);
  private readonly api = inject(IntegrationsApiService);

  start(_ctx: ConnectContext): Observable<ConnectResult> {
    return this.openOAuthPopup((code, stop$, finish) =>
      this.exchangeCode(code, stop$, finish),
    );
  }

  addChannel(integrationId: string): Observable<ConnectResult> {
    return this.openOAuthPopup((code, stop$, finish) =>
      this.exchangeCodeForChannel(integrationId, code, stop$, finish),
    );
  }

  private openOAuthPopup(
    onCode: (code: string, stop$: Subject<void>, finish: (result: ConnectResult) => void) => void,
  ): Observable<ConnectResult> {
    return new Observable<ConnectResult>((subscriber) => {
      const stop$ = new Subject<void>();
      const win = this.document.defaultView;
      if (!win) {
        subscriber.next({ kind: 'error', message: 'Não foi possível acessar o contexto do navegador.', recoverable: false });
        subscriber.complete();
        return;
      }

      const state = win.crypto.randomUUID();
      const popup = win.open(this.buildAuthorizeUrl(state), 'instagram_oauth', POPUP_FEATURES);
      if (!popup) {
        subscriber.next({ kind: 'error', message: 'O navegador bloqueou a janela do Instagram. Permita pop-ups e tente novamente.', recoverable: true });
        subscriber.complete();
        return;
      }

      let settled = false;
      // Set once the callback page relays its message. After this, the popup
      // closing is expected (the callback closes itself) and must NOT be read
      // as a cancellation while the async code exchange is still in flight.
      let callbackReceived = false;

      const finish = (result: ConnectResult) => {
        if (settled) return;
        settled = true;
        win.clearInterval(poll);
        subscriber.next(result);
        subscriber.complete();
      };

      const onMessage = (event: MessageEvent) => {
        if (event.origin !== win.location.origin) return;
        const data = event.data as InstagramOAuthCallbackMessage | undefined;
        if (data?.type !== IG_OAUTH_CALLBACK_TYPE || data.state !== state) return;

        callbackReceived = true;
        win.clearInterval(poll);

        if (data.error || !data.code) {
          finish({ kind: 'cancelled' });
          return;
        }
        onCode(data.code, stop$, finish);
      };

      win.addEventListener('message', onMessage);

      // The popup carries no opener message when the user just closes it without
      // authorizing — poll for closure to resolve as cancelled instead of hanging.
      // Once the callback message arrives, closure is expected and ignored.
      const poll = win.setInterval(() => {
        if (popup.closed && !callbackReceived) finish({ kind: 'cancelled' });
      }, POPUP_POLL_MS);

      return () => {
        win.removeEventListener('message', onMessage);
        win.clearInterval(poll);
        if (!popup.closed) popup.close();
        stop$.next();
        stop$.complete();
      };
    });
  }

  private buildAuthorizeUrl(state: string): string {
    const params = new URLSearchParams({
      client_id: environment.instagramAppId,
      redirect_uri: environment.instagramRedirectUri,
      response_type: 'code',
      scope: IG_SCOPE,
      state,
      force_reauth: 'true',
    });
    return `${IG_AUTHORIZE_URL}?${params.toString()}`;
  }

  private exchangeCode(code: string, stop$: Subject<void>, finish: (result: ConnectResult) => void): void {
    this.api
      .connectInstagram({ authCode: code })
      .pipe(takeUntil(stop$))
      .subscribe({
        next: (resp) => finish({ kind: 'success', integrationId: resp.integration.id }),
        error: (err: { error?: { message?: string } }) =>
          finish({
            kind: 'error',
            message: err?.error?.message ?? 'Falha ao concluir a conexão com o Instagram.',
            recoverable: true,
          }),
      });
  }

  private exchangeCodeForChannel(
    integrationId: string,
    code: string,
    stop$: Subject<void>,
    finish: (result: ConnectResult) => void,
  ): void {
    this.api
      .syncOAuthChannel(integrationId, { authCode: code })
      .pipe(takeUntil(stop$))
      .subscribe({
        next: (resp) => finish({ kind: 'success', integrationId: resp.integration.id }),
        error: (err: { error?: { message?: string } }) =>
          finish({
            kind: 'error',
            message: err?.error?.message ?? 'Falha ao adicionar a conta Instagram.',
            recoverable: true,
          }),
      });
  }
}
